import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  commissionsApi,
  type CommissionClaimDetailDto,
  type ContestClaimRequest,
} from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { CONTESTABLE_FIELDS } from "./contestableFields";

interface ContestModalProps {
  open: boolean;
  onClose: () => void;
  claim: CommissionClaimDetailDto;
}

export function ContestModal({ open, onClose, claim }: ContestModalProps) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [flagged, setFlagged] = useState<Record<string, boolean>>({});
  const [suggGross, setSuggGross] = useState("");
  const [suggPct, setSuggPct] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const contestedFields = Object.entries(flagged)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const suggested: Record<string, unknown> = {};
      if (suggGross) suggested["GrossRevenue"] = Number(suggGross);
      if (suggPct) suggested["CommissionPercent"] = Number(suggPct);
      const body: ContestClaimRequest = {
        notes: notes.trim(),
        contestedFields,
        suggestedValues: Object.keys(suggested).length > 0 ? suggested : null,
      };
      return commissionsApi.contest(claim.summary.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const canSubmit =
    notes.trim().length > 0 && Object.values(flagged).some(Boolean) && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Contest claim ${claim.summary.claimNumber}`}
      description="Flag at least one field, explain what you believe is correct, and optionally propose values. The team member sees your notes and can accept suggestions with one click."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Contest claim
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col gap-space-4">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Fields in dispute
          </div>
          <div className="flex flex-col gap-space-2">
            {CONTESTABLE_FIELDS.map((f) => (
              <label key={f.key} className="flex items-center gap-space-2 text-small cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!flagged[f.key]}
                  onChange={(e) => setFlagged({ ...flagged, [f.key]: e.target.checked })}
                />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
        </div>

        <FormField label="Contest notes" required helperText="Explain what's wrong and what you believe is correct.">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>

        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Suggested corrections (optional)
          </div>
          <div className="grid grid-cols-2 gap-space-3">
            <FormField label="Suggested gross revenue">
              <Input
                type="number"
                step="0.01"
                min={0}
                value={suggGross}
                onChange={(e) => setSuggGross(e.target.value)}
                placeholder="0.00"
              />
            </FormField>
            <FormField label="Suggested commission %">
              <Input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={suggPct}
                onChange={(e) => setSuggPct(e.target.value)}
                placeholder="10.00"
              />
            </FormField>
          </div>
          <p className="text-caption text-gray-mid mt-space-1">
            If supplied, the team member can accept these with one click to generate the revised claim.
          </p>
        </div>
      </div>
    </Modal>
  );
}
